/**
 * Customer List Page - Client Requirements
 *
 * Shows all installer companies with:
 * - Customer Name, Email, Phone, Address, Status
 * - Search functionality
 * - Filters (Status, Region)
 * - Action buttons: Edit, Delete, Details
 * - CSV Export
 */

import PageHeader from '@/components/common/PageHeader';

import { ICommonQueryParams } from '@/types';
import { DEFAULT_STARTING_PAGE, REGION_ALL } from '@/utils/constant/appConstant';
import { DEFAULT_PAGE_SIZE } from '../constants';
import { exportToCSV, generateCSVFilename } from '@/utils/csv';
import { Button } from 'antd';
import React, { useEffect, useState } from 'react';
import { IoCloudDownloadOutline } from 'react-icons/io5';
import { useGetCurrentUser } from '@/hooks/app';
import { CustomerTable } from '../components/table';
import { CustomerFilters } from '../components';

import {
  combineFilterQueries,
  generateCustomersSearchQuery,
  generateRegionFilterQuery,
  generateStatusFilterQuery,
  generateCustomerTypeFilterQuery,
  generateCreditTermsFilterQuery
} from '../utils';
import { useCustomerList } from '../hooks';


export const CustomerListPage: React.FC = () => {
  const { data: currentUser } = useGetCurrentUser();
  const isRegionAll = Boolean(currentUser?.currentUser?.dogma_region && currentUser?.currentUser?.dogma_region === REGION_ALL);

  const [queryParams, setQueryParams] = useState<ICommonQueryParams>({
    current: DEFAULT_STARTING_PAGE,
    pageSize: DEFAULT_PAGE_SIZE,
    filter: '',
  });
  const [currentSearchText, setCurrentSearchText] = useState<string>('');
  const [currentStatus, _] = useState<number | null>(null);
  const [currentRegion, setCurrentRegion] = useState<number | null>(null);
  const [currentCustomerType, setCurrentCustomerType] = useState<number | null>(null);
  const [currentCreditTerms, setCurrentCreditTerms] = useState<number | null>(null);

  // Set default region filter for users without "all regions" access
  useEffect(() => {
    if (currentUser?.currentUser?.dogma_region && !isRegionAll && currentRegion === null) {
      setCurrentRegion(currentUser.currentUser.dogma_region);
    }
  }, [currentUser, isRegionAll, currentRegion]);

  const defaultRegionFilters = isRegionAll ? "" : generateRegionFilterQuery(currentUser?.currentUser?.dogma_region);

  // Fetch data using hooks
  const { data: customerListResponse, isLoading } = useCustomerList({
    queryParams: {
      ...queryParams,
      filter: queryParams.filter ? queryParams.filter : (defaultRegionFilters ? `&$filter=${defaultRegionFilters}` : '')
    },
    enabled: Boolean(currentUser?.currentUser?.systemuserid)
  });

  const customers = customerListResponse?.value || [];

  const handleTableChange = (current: number, pageSize: number) => {
    setQueryParams((prev) => ({ ...prev, current, pageSize }));
  };

  // Handle search text change
  const handleSearchTextChange = (searchText: string) => {
    setCurrentSearchText(searchText);

    const searchQuery = searchText ? generateCustomersSearchQuery(searchText) : "";
    const statusQuery = (currentStatus !== null && currentStatus !== undefined) ? generateStatusFilterQuery(currentStatus) : "";
    const regionQuery = (currentRegion !== null && currentRegion !== undefined) ? generateRegionFilterQuery(currentRegion) : "";
    const customerTypeQuery = (currentCustomerType !== null && currentCustomerType !== undefined) ? generateCustomerTypeFilterQuery(currentCustomerType) : "";
    const creditTermsQuery = (currentCreditTerms !== null && currentCreditTerms !== undefined) ? generateCreditTermsFilterQuery(currentCreditTerms) : "";
    const combinedFilter = combineFilterQueries([
      searchQuery?.replace("&$filter=", ""),
      statusQuery,
      regionQuery,
      customerTypeQuery,
      creditTermsQuery
    ]);

    setQueryParams({
      current: DEFAULT_STARTING_PAGE,
      pageSize: queryParams.pageSize,
      filter: combinedFilter ? `&$filter=${combinedFilter}` : '',
    });
  };

  // Handle region filter change
  const handleRegionFilterChange = (region: number | null) => {
    setCurrentRegion(region);

    const searchQuery = currentSearchText ? generateCustomersSearchQuery(currentSearchText) : "";
    const statusQuery = (currentStatus !== null && currentStatus !== undefined) ? generateStatusFilterQuery(currentStatus) : "";
    const regionQuery = (region !== null && region !== undefined) ? generateRegionFilterQuery(region) : "";
    const customerTypeQuery = (currentCustomerType !== null && currentCustomerType !== undefined) ? generateCustomerTypeFilterQuery(currentCustomerType) : "";
    const creditTermsQuery = (currentCreditTerms !== null && currentCreditTerms !== undefined) ? generateCreditTermsFilterQuery(currentCreditTerms) : "";
    const combinedFilter = combineFilterQueries([
      searchQuery?.replace("&$filter=", ""),
      statusQuery,
      regionQuery,
      customerTypeQuery,
      creditTermsQuery
    ]);

    setQueryParams({
      current: DEFAULT_STARTING_PAGE,
      pageSize: queryParams.pageSize,
      filter: combinedFilter ? `&$filter=${combinedFilter}` : '',
    });
  };

  // Handle customer type filter change
  const handleCustomerTypeFilterChange = (customerType: number | null) => {
    setCurrentCustomerType(customerType);

    const searchQuery = currentSearchText ? generateCustomersSearchQuery(currentSearchText) : "";
    const statusQuery = (currentStatus !== null && currentStatus !== undefined) ? generateStatusFilterQuery(currentStatus) : "";
    const regionQuery = (currentRegion !== null && currentRegion !== undefined) ? generateRegionFilterQuery(currentRegion) : "";
    const customerTypeQuery = (customerType !== null && customerType !== undefined) ? generateCustomerTypeFilterQuery(customerType) : "";
    const creditTermsQuery = (currentCreditTerms !== null && currentCreditTerms !== undefined) ? generateCreditTermsFilterQuery(currentCreditTerms) : "";
    const combinedFilter = combineFilterQueries([
      searchQuery?.replace("&$filter=", ""),
      statusQuery,
      regionQuery,
      customerTypeQuery,
      creditTermsQuery
    ]);

    setQueryParams({
      current: DEFAULT_STARTING_PAGE,
      pageSize: queryParams.pageSize,
      filter: combinedFilter ? `&$filter=${combinedFilter}` : '',
    });
  };

  // Handle credit terms filter change
  const handleCreditTermsFilterChange = (creditTerms: number | null) => {
    setCurrentCreditTerms(creditTerms);

    const searchQuery = currentSearchText ? generateCustomersSearchQuery(currentSearchText) : "";
    const statusQuery = (currentStatus !== null && currentStatus !== undefined) ? generateStatusFilterQuery(currentStatus) : "";
    const regionQuery = (currentRegion !== null && currentRegion !== undefined) ? generateRegionFilterQuery(currentRegion) : "";
    const customerTypeQuery = (currentCustomerType !== null && currentCustomerType !== undefined) ? generateCustomerTypeFilterQuery(currentCustomerType) : "";
    const creditTermsQuery = (creditTerms !== null && creditTerms !== undefined) ? generateCreditTermsFilterQuery(creditTerms) : "";
    const combinedFilter = combineFilterQueries([
      searchQuery?.replace("&$filter=", ""),
      statusQuery,
      regionQuery,
      customerTypeQuery,
      creditTermsQuery
    ]);

    setQueryParams({
      current: DEFAULT_STARTING_PAGE,
      pageSize: queryParams.pageSize,
      filter: combinedFilter ? `&$filter=${combinedFilter}` : '',
    });
  };



  const handleApplyAllFilters = ({ region, customerType, creditTerms }: {
    region: number | null;
    customerType: number | null;
    creditTerms: number | null;
  }) => {
    setCurrentRegion(region);
    setCurrentCustomerType(customerType);
    setCurrentCreditTerms(creditTerms);

    const searchQuery = currentSearchText ? generateCustomersSearchQuery(currentSearchText) : "";
    const statusQuery = (currentStatus !== null && currentStatus !== undefined) ? generateStatusFilterQuery(currentStatus) : "";
    const regionQuery = (region !== null && region !== undefined) ? generateRegionFilterQuery(region) : "";
    const customerTypeQuery = (customerType !== null && customerType !== undefined) ? generateCustomerTypeFilterQuery(customerType) : "";
    const creditTermsQuery = (creditTerms !== null && creditTerms !== undefined) ? generateCreditTermsFilterQuery(creditTerms) : "";
    const combinedFilter = combineFilterQueries([
      searchQuery?.replace("&$filter=", ""),
      statusQuery,
      regionQuery,
      customerTypeQuery,
      creditTermsQuery,
    ]);

    setQueryParams({
      current: DEFAULT_STARTING_PAGE,
      pageSize: queryParams.pageSize,
      filter: combinedFilter ? `&$filter=${combinedFilter}` : '',
    });
  };

  // Clear all filters
  const handleClearAllFilters = () => {
    setCurrentRegion(isRegionAll ? null : (currentUser?.currentUser?.dogma_region || null));
    setCurrentCustomerType(null);
    setCurrentCreditTerms(null);
    setCurrentSearchText('');
    setQueryParams({
      current: DEFAULT_STARTING_PAGE,
      pageSize: queryParams.pageSize,
      filter: defaultRegionFilters ? `&$filter=${defaultRegionFilters}` : '',
    });
  };

  const handleExportCSV = () => {
    const data = customers.map((customer) => ({
      'Customer Name': customer.name,
      Email: customer.primarycontactid?.emailaddress1 || '-',
      Phone: customer.primarycontactid?.telephone1 || '-',
      Address: customer.address1_composite || '-',
      Region: customer['dogma_region@OData.Community.Display.V1.FormattedValue'] || '-',
      Status: customer['statecode@OData.Community.Display.V1.FormattedValue'] || '-',
    }));

    const columns = [
      { key: 'Customer Name' as const, header: 'Customer Name' },
      { key: 'Email' as const, header: 'Email' },
      { key: 'Phone' as const, header: 'Phone' },
      { key: 'Address' as const, header: 'Address' },
      { key: 'Region' as const, header: 'Region' },
      { key: 'Status' as const, header: 'Status' },
    ];

    const filename = generateCSVFilename('customer-list');
    exportToCSV(data, columns, filename);
  };

  return (
    <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
      <PageHeader
        title="Customer List"
        subtitle="Manage installer companies"
        breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Customers' }]}
        actions={

          <Button
            icon={<IoCloudDownloadOutline />}
            onClick={handleExportCSV}
          >
            Export CSV ({customers.length} records)
          </Button>

        }
      />

      {/* Filters */}
      <CustomerFilters
        currentSearchText={currentSearchText}
        currentRegion={currentRegion}
        currentCustomerType={currentCustomerType}
        currentCreditTerms={currentCreditTerms}
        isRegionAll={isRegionAll}
        userRegion={currentUser?.currentUser?.dogma_region}
        onSearchChange={handleSearchTextChange}
        onRegionChange={handleRegionFilterChange}
        onCustomerTypeChange={handleCustomerTypeFilterChange}
        onCreditTermsChange={handleCreditTermsFilterChange}
        onApplyFilters={handleApplyAllFilters}
        onClearAll={handleClearAllFilters}
      />

      {/* Table */}
      <CustomerTable
        isLoading={isLoading}
        data={customers}
        current={queryParams.current}
        pageSize={queryParams.pageSize}
        total={customerListResponse?.['@odata.count']}
        nextLink={customerListResponse?.['@odata.nextLink']}
        totalRecordCountLimitExceeded={customerListResponse?.['@Microsoft.Dynamics.CRM.totalrecordcountlimitexceeded']}
        handleChange={handleTableChange}
      />
    </div>
  );
};

export default CustomerListPage;
